/**
 * **打印包** —— 第一个真正的**领域代码包**（`kind: 'domain'`，可卸载）。
 *
 * ## 它贡献三样东西
 *
 * | 贡献 | 内容 | 在哪 |
 * |---|---|---|
 * | **节点** | `print.page` / `print.component.text` / `print.component.box` | `nodes.ts` |
 * | **求值器** | 页面值 / 组件值（将来输出适配器的输入） | `evaluators.ts` |
 * | **编辑器** | `print.layout`「页面编辑器」+ 它自己的两个侧栏面板 | `PageEditor.ts` / `panels.ts` |
 *
 * ## 为什么「版面」由包提供，而不是内核的第 9 个编辑器
 *
 * 内核原来有一条 `canvas`（「版面」，`implemented: false`）—— **已删除**。
 * 理由是 `package-boundaries.md` §10 那条判据：「换个域还要不要它」：
 *
 *   · 纸张 / dpi / 静区 / 组件种类 —— 只有打印域说得清；
 *   · UI 设计域的画布长的是另一副样子（约束 / 自适应 / 组件树）；
 *   · 而**通用**的那一半（指针 / 视口 / 命中 / 框选）已经在 `graph/` 里了。
 *
 * ⚠️ 所以这个包的编辑器**不是**"往通用编辑器里塞打印功能"（那是 §4 明确不做
 * 的 B 路），而是「注册自己的窗口类型」那条 A 路 —— 宿主只负责摆位置
 * （Area / Region / 侧栏壳），窗口里画什么完全归包。
 *
 * ## 它是 `domain`，所以能卸载
 *
 * 卸载之后：画布上的 `print.*` 节点**照常显示**（文档里逐节点存了 rows），
 * 只是算不出值（诊断说清缺什么）；页面编辑器那个窗口**回落到默认编辑器**，
 * 但**意图留着** —— 装回来窗口自己就回来（`editorRegistry.ts` 的三选一）。
 * 这正是 `node-packages.md` §6.11 那两条对称行为，一行都没为打印特例。
 */
import type { EditorContribution } from '@/types/editor'
import type { NodePackage } from '@/node/package'
import { PRINT_EVALUATORS } from './evaluators'
/* ★ 示例文档：一份"打开就能看懂"的幸运饼干签（见 examples.ts 的文件头） */
import { PRINT_EXAMPLES } from './examples'
import { PRINT_NODE_TYPES } from './nodes'
import { PrintPageEditor } from './PageEditor'
import {
  PrintElementPanel,
  PrintHeaderControls,
  PrintLayersPanel,
  PrintPagePanel,
  PrintPagesPanel,
} from './panels'

/** 页面编辑器贡献的编辑器类型标识（**带命名空间**，注册表的硬要求） */
export const PRINT_LAYOUT_EDITOR = 'print.layout'

export const PRINT_LAYOUT_CONTRIBUTION: EditorContribution = {
  /*
   * ⚠️ 为什么不是 `print.page`：那是**节点**类型标识。两个命名空间
   * （编辑器类型 / 节点类型）本来互不相干，但同名会让读代码的人
   * 以为 `EDITOR_META['print.page']` 和 `nodeTypeOf('print.page')`
   * 是同一个东西 —— 而它们一个是窗口、一个是数据。
   */
  type: PRINT_LAYOUT_EDITOR,
  label: '页面编辑器',
  /*
   * 图标沿用内核那条「版面」的（一个带横线的纸）——
   * 它认的就是"一页纸上的版面"这件事，删掉那条时把它接过来最自然。
   */
  icon: '<rect x="4" y="3" width="16" height="18" rx="2"/><path d="M4 8h16M8 12h8M8 16h5"/>',
  /**
   * 序号显式给 **9**（内核那 8 条之后的第一号）。
   *
   * ⚠️ 不给也能自动分配（`max(内核)+1`），但内置包显式写下来更稳：
   * 哪天内核又加一条，自动分配的号会漂，而顺序是用户看得见的东西。
   *
   * ⚠️ 2027 从 8 改成 9：内核加了第 8 条「文档」（文档库 / 首页），
   * 这条贡献当场撞号 —— 注册表**不会静默**：它自动让位并记一条问题
   * （`EDITOR_PROBLEMS`），而 `verify:packages` 有一条断言盯着
   * "重算之后问题清单回到空"。规矩是**内核占的号优先，包让位**。
   */
  digit: 9,
  /* 它不是图：只支持 header（标题栏必须有，否则那个窗口切不出去也关不掉） */
  regions: ['header'],
  defaultRegions: { minimap: false },
  /* 窗口头部的视图控件（缩放 / 编辑-预览）—— 工具栏只留"加东西" */
  header: PrintHeaderControls,
  /*
   * 底部提示条上的操作方式（真机反馈："快捷条里边没有页面编辑器的提示"）。
   *
   * ⚠️ 只写**这一版真的支持**的：滚轮 / 双指缩放、拖动吸附、Del 删除、
   * Ctrl+Z 撤销。⚠️ 特意**没有**"方向键微调" —— 键盘一律归宿主
   * （见 `PageEditor` 文件头：画布不监听键盘，`verify:layers` 有断言盯着），
   * 而宿主目前没有"方向键微调元素"这条命令；写上去就是骗人。
   */
  tips: [
    { keys: '滚轮', label: '缩放', touchKeys: '双指', touchLabel: '捏合缩放' },
    { keys: '拖动', label: '移动组件（吸网格 / 边缘 / 中线）', touchKeys: '单指拖', touchLabel: '移动组件（吸网格 / 边缘 / 中线）' },
    /*
     * ⚠️ 这两条**没有触摸端说法**：手机上没有 Del、也没有 Ctrl+Z
     * （宿主那两条命令都是键盘来的）。按 `touchKeys` 缺省即隐藏处理 ——
     * 在手机上写"Del 删掉选中的组件"比不写更糟。
     */
    { keys: 'Del', label: '删掉选中的组件' },
    { keys: 'Ctrl+Z', label: '撤销' },
    { keys: 'Shift', label: '多选（点空白处取消）', touchKeys: '长按', touchLabel: '多选' },
  ],
  panels: {
    left: [
      {
        id: 'page',
        label: '文档',
        icon: '<rect x="5" y="3" width="14" height="18" rx="2"/><path d="M9 7h6M9 11h6M9 15h4"/>',
        component: PrintPagePanel,
      },
      {
        /* ⚠️ 多页管理单独一页：纸张（文档）和"有哪些页"是两件事 */
        id: 'pages',
        label: '页面',
        icon: '<path d="M7 3h7l4 4v14H7z"/><path d="M14 3v4h4"/><path d="M10 12h6M10 16h6"/>',
        component: PrintPagesPanel,
      },
    ],
    right: [
      {
        id: 'element',
        label: '元素',
        icon: '<rect x="4" y="4" width="16" height="16" rx="2"/><path d="M4 10h16M10 4v16"/>',
        component: PrintElementPanel,
      },
      {
        /* ⚠️ 图层 = 当前页的组件顺序（层序就是 `items.N`，没有独立的 z-index） */
        id: 'layers',
        label: '图层',
        icon: '<path d="M12 3l9 5-9 5-9-5z"/><path d="M3 13l9 5 9-5"/>',
        component: PrintLayersPanel,
      },
    ],
  },
  component: PrintPageEditor,
}

export const PRINT_PACKAGE: NodePackage = {
  id: 'print',
  /*
   * ⚠️ 名字里带**热敏**：这一包目前只对热敏机（203/300dpi、1-bit、TSPL/ESC-POS
   * 那一族）负责。真机反馈原话："对打印这个节点包的名称改成『热敏打印节点包』，
   * 后面如果我们做了其他打印机的，再考虑抽象所有打印" ——
   * 也就是**先别急着抽象**：等真有第二类打印机（激光 / 喷墨 / 彩色热转印）
   * 时，再把公共那部分提出来，而不是现在猜一套。
   */
  label: '热敏打印',
  kind: 'domain',
  /*
   * ★ **包自己的分类图标**：机身 + 出纸口 + 一张纸。
   *
   * ⚠️ 它是**兜底**：节点类型自己声明了 `icon` 就用那一个，没声明的落到这里
   * （见 `node/nodeIcons.ts` 的两级回退）。内核那张 `CATEGORY_ICONS` 里
   * 已经有一条 `print`，但那是"内核替所有包猜的"——
   * 域包想把自己的分类画准，就在自己的 `categories` 里声明（**优先**）。
   * 画法三条约束同上（24×24、只有 stroke、线宽靠 CSS）。
   */
  categories: [
    {
      id: 'print',
      label: '热敏打印',
      icon: '<path d="M7 9.5V4.5H17V9.5"/><rect x="3.5" y="9.5" width="17" height="7.5" rx="2"/><path d="M7 13.2H17"/><path d="M7.5 17H16.5V20.5H7.5z"/>',
    },
  ],
  nodeTypes: PRINT_NODE_TYPES,
  evaluators: PRINT_EVALUATORS,
  /* ★ 示例文档（「文件 → 示例文件」那一栏的清单，宿主去接线） */
  examples: PRINT_EXAMPLES,
  editors: [PRINT_LAYOUT_CONTRIBUTION],
}
